import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface User {
  id: string;
  user_id?: string;
  email: string;
  name?: string;
  age?: number;
  gender?: string;
  bio?: string;
  role?: string;
  role_id?: string;
  role_name?: string;
  organization_id?: string;
  contact_number?: string;
  avatar_image?: string;
  telecaller_status?: string;
  user_status?: 'active' | 'inactive' | string;
  exotel_number?: string;
  supplier_id?: string;
  created_at: string;
  privileges?: string[];
  zone_ids?: string[];
  hq_ids?: string[];
  zones?: any[];
  headquarters?: any[];
  is_active?: boolean;
}

export interface UserDropdownItem {
  id: string;
  email: string;
  name?: string;
}

export interface RoleWithUsersDropdownItem {
  role_id: string;
  role_name: string;
  is_default: boolean;
  users: UserDropdownItem[];
}

export interface RolesWithUsersDropdownResponse {
  roles: RoleWithUsersDropdownItem[];
}

export interface CreateUserRequest {
  email: string;
  name?: string;
  age?: number;
  gender?: string;
  bio?: string;
  role_id?: string;
  role?: string;
  contact_number?: string;
  avatar_image?: string;
  password?: string;
  telecaller_status?: string;
  user_status?: 'active' | 'inactive' | string;
  exotel_number?: string;
  zone_ids?: string[];
  hq_ids?: string[];
  supplier_id?: string;
}

export interface UpdateUserRequest {
  email?: string;
  name?: string;
  age?: number;
  gender?: string;
  bio?: string;
  role_id?: string;
  role?: string;
  contact_number?: string;
  avatar_image?: string;
  telecaller_status?: string;
  user_status?: 'active' | 'inactive' | string;
  exotel_number?: string;
  zone_ids?: string[];
  hq_ids?: string[];
  supplier_id?: string;
}

export interface AllUsersResponse {
  users: User[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
    limit: number;
  };
}

export interface UserListResponse {
  users: User[];
  total: number;
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: User[];
    totalPages?: number;
    currentPage?: number;
    totalUsers?: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class UsersApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get users for the current user's organization with optional search
   * GET /api/v1/users/
   */
  getUsers(search?: string): Observable<UserListResponse> {
    const url = search
      ? `${this.apiUrl}/users/?search=${encodeURIComponent(search)}`
      : `${this.apiUrl}/users/`;
    return this.http.get<UserListResponse>(url);
  }

  /**
   * Get all users across all roles with full details
   * GET /api/v1/users/all
   * Requires privilege: view_users
   */
  getAllUsers(role?: string, page: number = 1, limit: number = 50): Observable<AllUsersResponse> {
    let url = `${this.apiUrl}/users/all?page=${page}&limit=${limit}`;
    if (role) url += `&role=${encodeURIComponent(role)}`;
    return this.http.get<AllUsersResponse>(url);
  }

  /**
   * Get a specific user by ID
   * GET /api/v1/users/{user_id}
   */
  getUser(userId: string): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/users/${userId}`);
  }

  /**
   * Get users belonging to a specific organization (legacy)
   * @deprecated This endpoint doesn't exist in the API spec
   */
  getUsersByOrganization(organizationId: string, search?: string): Observable<any> {
    const url = search
      ? `${this.apiUrl}/users/organization/${organizationId}/users?search=${encodeURIComponent(search)}`
      : `${this.apiUrl}/users/organization/${organizationId}/users`;
    return this.http.get<any>(url);
  }

  /**
   * Get roles with their attached users for dropdowns (current organization)
   * GET /api/v1/users/dropdowns/roles-users
   */
  getRolesWithUsersDropdown(search?: string): Observable<RolesWithUsersDropdownResponse> {
    const url = search
      ? `${this.apiUrl}/users/dropdowns/roles-users?search=${encodeURIComponent(search)}`
      : `${this.apiUrl}/users/dropdowns/roles-users`;
    return this.http.get<RolesWithUsersDropdownResponse>(url);
  }

  /**
   * Get roles with users for a sub-organization (super admin only)
   * GET /api/v1/users/organization/{organization_id}/dropdowns/roles-users
   */
  getRolesWithUsersDropdownForOrganization(organizationId: string, search?: string): Observable<RolesWithUsersDropdownResponse> {
    const url = search
      ? `${this.apiUrl}/users/organization/${organizationId}/dropdowns/roles-users?search=${encodeURIComponent(search)}`
      : `${this.apiUrl}/users/organization/${organizationId}/dropdowns/roles-users`;
    return this.http.get<RolesWithUsersDropdownResponse>(url);
  }

  /**
   * Get users by privilege group and/or specific privilege (current organization)
   * GET /api/v1/users/by-privilege
   */
  getUsersByPrivilege(
    privilegeGroup: string = 'all',
    privileges: string[] = ['all'],
    search?: string,
    page: number = 1,
    limit: number = 10
  ): Observable<UserListResponse> {
    let url = `${this.apiUrl}/users/by-privilege?privilege_group=${privilegeGroup}&page=${page}&limit=${limit}`;

    // Add privilege filters
    privileges.forEach(p => {
      url += `&privilege=${encodeURIComponent(p)}`;
    });

    // Add search if provided
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return this.http.get<UserListResponse>(url);
  }

  /**
   * Get users by privilege for a specific organization (super admin)
   * GET /api/v1/users/organization/{organization_id}/by-privilege
   */
  getUsersByPrivilegeForOrganization(
    organizationId: string,
    privilegeGroup: string = 'all',
    privileges: string[] = ['all'],
    search?: string,
    page: number = 1,
    limit: number = 10
  ): Observable<UserListResponse> {
    let url = `${this.apiUrl}/users/organization/${organizationId}/by-privilege?privilege_group=${privilegeGroup}&page=${page}&limit=${limit}`;

    // Add privilege filters
    privileges.forEach(p => {
      url += `&privilege=${encodeURIComponent(p)}`;
    });

    // Add search if provided
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return this.http.get<UserListResponse>(url);
  }

  /**
   * Create a new user in the current organization
   * POST /api/v1/users/
   */
  createUser(userData: CreateUserRequest): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/users/`, userData);
  }

  /**
   * Create a user in a sub-organization (super admin only)
   * POST /api/v1/users/organization/{organization_id}/users
   */
  createUserForOrganization(organizationId: string, userData: CreateUserRequest): Observable<User> {
    return this.http.post<User>(
      `${this.apiUrl}/users/organization/${organizationId}/users`,
      userData
    );
  }

  /**
   * Update an existing user
   * PATCH /api/v1/users/{user_id}
   */
  updateUser(userId: string, userData: UpdateUserRequest): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/users/${userId}`, userData);
  }

  /**
   * Update current user's own profile
   * PATCH /api/v1/users/me
   */
  updateCurrentUser(userData: UpdateUserRequest): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/users/me`, userData);
  }

  /**
   * Update a user for a specific organization (legacy - use updateUser instead)
   * @deprecated Use updateUser instead - the API doesn't have organization-specific update endpoint
   */
  updateUserForOrganization(
    organizationId: string,
    userId: string,
    userData: UpdateUserRequest
  ): Observable<any> {
    //console.log('Updating user for organization:', organizationId, userId, userData);
    return this.http.patch<any>(
      `${this.apiUrl}/users/organization/${organizationId}/users/${userId}`,
      userData
    );
  }

  /**
   * Delete a user
   * DELETE /api/v1/users/{user_id}
   */
  deleteUser(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/users/${userId}`);
  }

  /**
   * Delete a user for a specific organization (legacy - use deleteUser instead)
   * @deprecated Use deleteUser instead - the API doesn't have organization-specific delete endpoint
   */
  deleteUserForOrganization(organizationId: string, userId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.apiUrl}/users/organization/${organizationId}/users/${userId}`
    );
  }

  /**
   * Send password reset link to a specific user (org admin or super admin)
   * POST /api/v1/users/{user_id}/send-password-reset-link
   */
  sendPasswordResetLinkToUser(userId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/users/${userId}/send-password-reset-link`, {});
  }

  /**
   * Send reset password link to user (legacy)
   * @deprecated Use sendPasswordResetLinkToUser instead
   */
  sendResetPasswordLink(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/users/forgot-password`, { email });
  }

  /**
   * Get users by role search for dropdowns (legacy)
   * @deprecated Use getRolesWithUsersDropdown instead
   */
  getUsersByRoleDropdown(search: string = 'manager'): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/users/dropdowns/roles-users?search=${search}`);
  }

  /**
   * Admin send password reset link (uses auth endpoint)
   * POST /api/v1/auth/admin/send-password-reset-link
   */
  sendResetPasswordLinkAdmin(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/admin/send-password-reset-link`, { email });
  }
}
